import {Component, OnDestroy, OnInit} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {Location} from "@angular/common";
import {BehaviorSubject, Subject} from "rxjs";
import {filter, finalize, takeUntil, withLatestFrom} from "rxjs/operators";
import {DisclaimerComponent} from "../components/disclaimer.component";
import {Dialog} from "@angular/cdk-experimental/dialog";

interface FormData {
    name: string
    email: string
    password: string
    privacy: boolean
}

@Component({
    selector: "app-register",
    templateUrl: "./register.component.html",
    styleUrls: ["./register.component.scss"]
})
export class RegisterComponent implements OnInit, OnDestroy {
    private readonly register$ = new Subject<boolean>()
    public readonly submitting$ = new BehaviorSubject<boolean>(false)
    public readonly submitError$ = new BehaviorSubject<string | null>(null)
    private readonly destroyed$ = new Subject<boolean>()

    public readonly name = new FormControl(null, Validators.required)
    public readonly email = new FormControl(null, Validators.compose([Validators.required, Validators.email]))
    public readonly password = new FormControl(null, [Validators.required, Validators.minLength(8)])
    public readonly form: FormGroup

    constructor(private readonly http: HttpClient,
                private readonly location: Location,
                private readonly dialog: Dialog) {
        this.form = new FormGroup({
            name: this.name,
            email: this.email,
            password: this.password,
            privacy: new FormControl(false, Validators.requiredTrue)
        })
    }

    public ngOnInit() {
        this.register$
            .pipe(takeUntil(this.destroyed$))
            .pipe(withLatestFrom(this.form.valueChanges, this.form.statusChanges))
            .pipe(filter(([_, formData, status]) => status === "VALID"))
            .subscribe(([_, formData]) => this.sendRegistrationRequest(formData))
    }

    public register() {
        this.register$.next(true)
    }

    private sendRegistrationRequest(formData: FormData) {

        const userData = {
            ...formData,
            broker: "BELLMONT"
        }

        this.submitting$.next(true)
        this.submitError$.next(null)

        this.http
            .post("/tradefloor/public_api/signup", userData)
            .pipe(takeUntil(this.destroyed$))
            .pipe(finalize(() => this.submitting$.next(false)))
            .subscribe(() => this.redirectToApp(),
                (errorResponse) => this.registerError(errorResponse))
    }

    private redirectToApp() {
        window.location.assign("/tradefloor/#/")
    }

    private registerError(errorResponse: HttpErrorResponse) {
        this.submitError$.next(this.extractErrorMessage(errorResponse))
    }

    private extractErrorMessage(errorResponse: HttpErrorResponse) {
        switch (errorResponse.status) {
            case 409:
                return "Email address is already in use"
            case 503:
                return "We are performing maintenance. Please try again later."
            default:
                return "There was an error processing your request"
        }
    }

    public showTerms() {
        this.dialog.openFromComponent(DisclaimerComponent)
    }

    public ngOnDestroy(): void {
        this.destroyed$.next(true)
        this.destroyed$.complete()
    }
}
